import "./index.less";
import React from "react";
import Consignee from "./Consignee";
import OrderDTO from "@/dto/order/OrderDTO";
import ShopDTO from "@/dto/shop/ShopDTO";
import AbnormalCause, { shouldRenderAbnormalCause } from "./AbnormalCause";
import { observer } from "mobx-react-lite";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import OrderRemark from "../OrderRemark";
import OrderTableColumnsType from "@/scenes/OrderManagement/enum/OrderTableColumnsType";
import PddConsolidatedShipping, { shouldRenderPddConsolidatedShipping } from "./PddConsolidatedShipping";
import PddAvailableLogisticsRecommend, {
    shouldRenderPddAvailableLogisticsRecommend,
} from "./PddAvailableLogisticsRecommend";
import PddCourierDoorToDoorCollectTip, {
    shouldRenderPddCourierDoorToDoorCollectTip,
} from "./PddCourierDoorToDoorCollectTip";
import PddShipHoldTip, { shouldRenderPddShipHoldTip } from "./PddShipHoldTip";
import PendingShipmentCountInfo, { shouldRenderPendingShipmentCountInfo } from "./PendingShipmentCountInfo";
import PendingPaymentCountInfo, { shouldRenderPendingPaymentCountInfo } from "./PendingPaymentCountInfo";
import ShippingInfo, { shouldRenderShippingInfo } from "./ShippingInfo";
import OrderOnHoldInfo, { shouldRenderOrderOnHoldInfo } from "./OrderOnHoldInfo";
import RetailerShopTokenExpiredInfo from "./RetailerShopTokenExpiredInfo";
import ShopType from "@/enum/ShopType";
import DouDianConsignee from "./DouDianConsignee";
import CanNotShipTip, { shouldRenderCanNotShipTip } from "./CanNotShipTip";
import ChannelShopQualityInspectionTip, {
    shouldRenderChannelShopQualityInspectionTip,
} from "@/components/ChannelShopQualityInspectionTip";
import OrderStatus from "@/enum/OrderStatus";
import OrderTag from "@/enum/OrderTag";
import ChannelShopGreetingCardInfo, {
    shouldRenderChannelShopGreetingCardInfo,
} from "@/components/ChannelShopGreetingCardInfo";
import PrintShippingDisabledTip, { shouldRenderPrintShippingDisabledTip } from "./PrintShippingDisabledTip";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import PickupDeadlineWarningInfo, { shouldRenderPickupDeadlineWarningInfo } from "./PickupDeadlineWarningInfo";
import WiredOrderInfo, { shouldRenderWiredOrderInfo } from "./WiredOrderInfo";
import useAuthorityCheck from "@/hooks/useAuthorityCheck";
import Authority from "@/enum/Authority";

interface ConsigneeColumnProps {
    order: OrderDTO;
    shop: ShopDTO;
    tab: OrderTab;
}

interface ConsigneeWrapProps {
    order: OrderDTO;
    shop: ShopDTO;
}

const ConsigneeWrap: React.FC<ConsigneeWrapProps> = observer(function (props) {
    const { order, shop } = props;

    const orderManagementStore = useOrderManagementStore();
    const { hasAuthority } = useAuthorityCheck(Authority.ROLE_ORDER_PRIVACY_CONSIGNEE_DECRYPT);

    const retailerShopTokenExpired = order?.supplierDropShippingInfo?.retailerShopTokenExpired;

    if (retailerShopTokenExpired) {
        return <RetailerShopTokenExpiredInfo key="RetailerShopTokenExpiredInfo" order={order} />;
    }

    if (order.status === OrderStatus.PENDING_PAYMENT && order.tags?.includes(OrderTag.PRESENT)) {
        return <span className="error fb">收礼人领取后展示</span>;
    }

    if (!import.meta.env.VITE_IS_QN && shop.type === ShopType.DOUDIAN && hasAuthority) {
        return (
            <DouDianConsignee
                key="Consignee"
                order={order}
                onUpdateSuccess={() => {
                    orderManagementStore.fetchOrders(shop.id);
                }}
            />
        );
    }

    return (
        <Consignee
            key="Consignee"
            order={order}
            shop={shop}
            onUpdateSuccess={() => {
                orderManagementStore.fetchOrders(shop.id);
            }}
        />
    );
});

interface OrderRemarkWrapProps {
    order: OrderDTO;
}

const OrderRemarkWrap: React.FC<OrderRemarkWrapProps> = observer(function (props) {
    const { order } = props;

    const orderManagementStore = useOrderManagementStore();

    const { orderTableColumnMap } = orderManagementStore.storage;

    const showRemark = !orderTableColumnMap.get(OrderTableColumnsType.ORDER_REMARK)?.visible;

    if (!showRemark) {
        return null;
    }

    return <OrderRemark key="OrderRemark" order={order} onUpdateOrder={orderManagementStore.table.updateOrder} />;
});

// 收货人信息列
const ConsigneeColumn: React.FC<ConsigneeColumnProps> = ({ order, shop, tab }) => {
    const shouldShowOrderOnHoldInfo = shouldRenderOrderOnHoldInfo(order);
    const shouldShowShippingInfo = shouldRenderShippingInfo(order);
    const shouldShowPendingPaymentCountInfo = shouldRenderPendingPaymentCountInfo(order);
    const shouldShowPendingShipmentCountInfo = shouldRenderPendingShipmentCountInfo(order, shop);
    const shouldShowAbnormalCause = shouldRenderAbnormalCause(order);
    const shouldShowPddShipHoldTip = shouldRenderPddShipHoldTip(order, shop);
    const shouldShowPddAvailableLogisticsRecommend = shouldRenderPddAvailableLogisticsRecommend(order, shop);
    const shouldShowPddConsolidatedShipping = shouldRenderPddConsolidatedShipping(order, shop);
    const shouldShowPddCourierDoorToDoorCollectTip = shouldRenderPddCourierDoorToDoorCollectTip(order);
    const shouldShowCanNotShipTip = shouldRenderCanNotShipTip(order);
    const shouldShowPrintShippingDisabledTip = shouldRenderPrintShippingDisabledTip(order);
    const shouldShowChannelShopQualityInspectionTip = shouldRenderChannelShopQualityInspectionTip(order);
    const shouldShowChannelShopGreetingCardInfo = shouldRenderChannelShopGreetingCardInfo(order);
    const shouldShowPickupDeadlineWarningInfo = shouldRenderPickupDeadlineWarningInfo(shop, tab);
    const shouldShowWiredOrderInfo = shouldRenderWiredOrderInfo(order);

    return (
        <td>
            <ConsigneeWrap key="ConsigneeWrap" order={order} shop={shop} />

            <OrderRemarkWrap key="OrderRemarkWrap" order={order} />

            {shouldShowOrderOnHoldInfo ? <OrderOnHoldInfo key="OrderOnHoldInfo" order={order} /> : null}

            {shouldShowShippingInfo ? <ShippingInfo key="ShippingInfo" order={order} shop={shop} /> : null}

            {shouldShowPendingPaymentCountInfo ? (
                <PendingPaymentCountInfo key="PendingPaymentCountInfo" order={order} />
            ) : null}

            {shouldShowPendingShipmentCountInfo ? (
                <PendingShipmentCountInfo key="PendingShipmentCountInfo" order={order} shop={shop} />
            ) : null}

            {shouldShowAbnormalCause ? <AbnormalCause key="AbnormalCause" order={order} tab={tab} /> : null}

            {shouldShowPddShipHoldTip ? <PddShipHoldTip key="PddShipHoldTip" order={order} shop={shop} /> : null}

            {shouldShowPddAvailableLogisticsRecommend ? (
                <PddAvailableLogisticsRecommend key="PddAvailableLogisticsRecommend" order={order} shop={shop} />
            ) : null}

            {shouldShowPddConsolidatedShipping ? (
                <PddConsolidatedShipping key="PddConsolidatedShipping" order={order} shop={shop} />
            ) : null}

            {shouldShowPddCourierDoorToDoorCollectTip ? (
                <PddCourierDoorToDoorCollectTip key="PddCourierDoorToDoorCollectTip" order={order} />
            ) : null}

            {shouldShowCanNotShipTip ? <CanNotShipTip key="CanNotShipTip" order={order} /> : null}

            {shouldShowPrintShippingDisabledTip ? (
                <PrintShippingDisabledTip key="PrintShippingDisabledTip" order={order} />
            ) : null}

            {shouldShowChannelShopQualityInspectionTip ? (
                <ChannelShopQualityInspectionTip key="ChannelShopQualityInspectionTip" order={order} />
            ) : null}

            {shouldShowChannelShopGreetingCardInfo ? (
                <ChannelShopGreetingCardInfo key="ChannelShopGreetingCardInfo" order={order} />
            ) : null}

            {shouldShowPickupDeadlineWarningInfo ? (
                <PickupDeadlineWarningInfo order={order} shop={shop} tab={tab} />
            ) : null}

            {shouldShowWiredOrderInfo ? <WiredOrderInfo order={order} /> : null}
        </td>
    );
};

export default observer(ConsigneeColumn);
